import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ArrowLeft, Save, Trash } from "react-feather";
import { useSelector } from "react-redux";
import { loggedInUser } from "core/redux/slice/authSlice";
import { all_routes } from "Router/all_routes";
import * as Yup from "yup";
import { Popconfirm, Table, DatePicker, Select } from "antd";
import { toast } from "core/Operations/toast";
import {
  useGetDefaultEmpAllowanceDeductionMutation,
  useImportEmpAllowanceDeductionMutation,
} from "AppQuries/payroll/emp-allowance-deduction";
import { endOfMonth, startOfMonth, format } from "date-fns";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "Components/UI/Input";
import { render } from "@fullcalendar/core/preact";
import { useFormattedEmployees } from "core/Operations/formattedOption/useFormattedEmployeeOptions";
import {
  useFormattedAllowanceOption,
  useFormattedDeductionOption,
} from "core/Operations/formattedOption/useFormattedMasterOptions";
import { useGetEmployeeQuery } from "AppQuries/employee/employee_list";
import dayjs from "dayjs";
import { BackIcon } from "Components/UI/Icon/Icons";
const { RangePicker } = DatePicker;
const schema = Yup.object().shape({
  allowanceDeductionData: Yup.array().of(
    Yup.object().shape({
      uniqueId: Yup.string().required("Unique Id is required"),
      employeeName: Yup.string().required("Employee Name is required"),
      type: Yup.string().required("Type is required"),
      description: Yup.string().required("Description is required"),
      amount: Yup.number()
        .typeError("Amount must be a number")
        .required("Amount is required")
        .min(0, "Amount must be positive"),
    })
  ),
});

const GenerateAllowanceAndDeduction = () => {
  const userData = useSelector(loggedInUser);
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState({
    companyCode: userData?.companyCode,
    locationCode: userData?.locationCode,
    fromDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    toDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
    salaryType: "",
  });
  const { data: employees } = useGetEmployeeQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
    departmentCode: 0,
  });

  const employeeOptions =
    employees
      ?.filter((emp) => emp.isActive)
      ?.map((emp) => ({
        id: emp.uniqueId,
        label: emp.fullName,
        value: emp.employeeCode,
      })) || [];

  const addiitonOptions = useFormattedAllowanceOption(userData?.companyCode);

  const deductionOptions = useFormattedDeductionOption(userData?.companyCode);
  const { mutate: importAllowanceAndDeduction, isPending } =
    useImportEmpAllowanceDeductionMutation();

  const methods = useForm({
    defaultValues: {
      allowanceDeductionData: [],
    },
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isSubmitted },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "allowanceDeductionData",
    keyName: "fieldId", // internal key for react-hook-form
  });

  const { mutate: generateAllowanceAndDeduction, isLoading } =
    useGetDefaultEmpAllowanceDeductionMutation();

  const generateData = () => {
    console.log("Query Params:", queryParams);
    generateAllowanceAndDeduction(queryParams, {
      onSuccess: (res) => {
        if (res.length > 0) {
          toast.success("Allowance And Deduction Generated Successfully.");
          setValue(
            "allowanceDeductionData",
            res.map((item, index) => ({
              ...item,
              newRow: false,
              rowId: `${Date.now()}-${index}`, // stable row identifier
            }))
          );
        } else {
          setValue("allowanceDeductionData", []);
          toast.warning("No Data Found.");
        }
      },
      onError: (error) => {
        toast.error(error.message || "Something went wrong.");
      },
    });
  };

  // Delete row by index
  // Delete row by unique id
  const deleteHandler = (rowId) => {
    const index = fields.findIndex((item) => item.rowId === rowId);
    if (index !== -1) remove(index);
  };

  // Add new empty row with unique id
  const addNewRowHandler = () => {
    const newId =
      fields.length > 0 ? Math.max(...fields.map((f) => f.id ?? 0)) + 1 : 0;
    append({
      uniqueId: "",
      employeeName: "",
      type: "",
      description: "",
      amount: "",
      rowId: `${Date.now()}-${fields.length}`,
      newRow: true,
    });
  };
  // Form submit
  const onSubmit = (data) => {
    const requestData = data.allowanceDeductionData.map((item) => ({
      companyCode: item.companyCode,
      uniqueId: item.uniqueId,
      adjType: item.type === "Addition" ? 1 : 0,
      adjName: item.description,
      employeeCode: item.employeeCode,
      amount: item.amount,
      departmentCode: "",
      locationCode: userData?.locationCode,
      createUser: userData?.userName,
      isActive: true,
      isTemporary: true,
      effectiveDateFrom: item.newRow
        ? format(startOfMonth(new Date()), "yyyy-MM-dd")
        : format(startOfMonth(new Date(item.date)), "yyyy-MM-dd"),
      effectiveDateTo: item.newRow
        ? format(endOfMonth(new Date()), "yyyy-MM-dd")
        : format(endOfMonth(new Date(item.date)), "yyyy-MM-dd"),
    }));

    importAllowanceAndDeduction(requestData, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          toast.success(
            data[0].errorMessage ||
              "Allowance And Deduction Imported Successfully."
          );
          navigate(`${all_routes.emp_allowance_deduction}`);
        } else {
          toast.warning(data[0].errorMessage || "Something went wrong.");
        }
      },
      onError: (error) => {
        toast.warning(error.message || "Something went wrong.");
      },
    });
  };

  const updateQueryParams = (key, value) => {
    setQueryParams((prev) => ({ ...prev, [key]: value }));
  };

  const employeeSelectHandler = (e, index) => {
    const selectedEmployee = employees.find(
      (emp) => emp.employeeCode === e.value
    );

    setValue(`allowanceDeductionData[${index}].employeeCode`, e.value);
    setValue(`allowanceDeductionData[${index}].employeeName`, e.label);
    setValue(
      `allowanceDeductionData[${index}].uniqueId`,
      selectedEmployee.uniqueId || ""
    );
    setValue(
      `allowanceDeductionData[${index}].companyCode`,
      selectedEmployee.companyCode || ""
    );
  };
  const columns = [
    {
      title: "Unique Id",
      dataIndex: "uniqueId",
      key: "uniqueId",
      render: (text, record, index) =>
        record.newRow ? (
          <Input
            name={`allowanceDeductionData[${index}].uniqueId`}
            className={`form-control ${
              errors.allowanceDeductionData?.[index]?.uniqueId
                ? "is-invalid"
                : ""
            }`}
            type="text"
            readOnly={true}
            style={{ width: "100%" }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (text, record, index) =>
        record.newRow ? (
          <Input
            name={`allowanceDeductionData[${index}].employeeName`}
            className={`form-control ${
              errors.allowanceDeductionData?.[index]?.employeeName
                ? "is-invalid"
                : ""
            }`}
            type="commonSelect"
            optionData={employeeOptions}
            defaultValue={watch(
              `allowanceDeductionData[${index}].employeeName`
            )}
            labelInValue
            onSelect={(e) => employeeSelectHandler(e, index)}
            style={{ width: "250px" }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text, record, index) =>
        record.newRow ? (
          <Input
            name={`allowanceDeductionData[${index}].type`}
            optionData={[
              { label: "Addition", value: "Addition" },
              { label: "Deduction", value: "Deduction" },
            ]}
            className={`form-control ${
              errors.allowanceDeductionData?.[index]?.type ? "is-invalid" : ""
            }`}
            type="commonSelect"
            style={{ width: "150px" }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text, record, index) =>
        record.newRow ? (
          <Input
            name={`allowanceDeductionData[${index}].description`}
            className={`form-control ${
              errors.allowanceDeductionData?.[index]?.description
                ? "is-invalid"
                : ""
            }`}
            optionData={
              watch(`allowanceDeductionData[${index}].type`) === "Addition"
                ? addiitonOptions
                : watch(`allowanceDeductionData[${index}].type`) === "Deduction"
                ? deductionOptions
                : []
            }
            labelInValue
            onSelect={(e) => {
              setValue(`allowanceDeductionData[${index}].description`, e.label);
              // setValue(`allowanceDeductionData[${index}].uniqueId`, e.value);
            }}
            type="commonSelect"
            style={{ width: "200px" }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record, index) => (
        // record.newRow ? (
        <Input
          name={`allowanceDeductionData[${index}].amount`}
          className={`form-control ${
            errors.allowanceDeductionData?.[index]?.amount ? "is-invalid" : ""
          }`}
          type="text"
          style={{ width: "100%" }}
        />
      ),
      // ) : (
      //   text
      // ),
    },
    {
      title: "Days",
      dataIndex: "days",
    },
    {
      title: "Action",
      render: (_, record) => (
        <Popconfirm
          title="Delete the row?"
          description="Really want to delete this row?"
          onConfirm={() => deleteHandler(record.rowId)}
          okText="Yes"
          cancelText="No"
        >
          <button className="btn btn-sm btn-outline-danger" type="button">
            <Trash />
          </button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Generate Employee Allowance And Deduction</h4>
              <h6>Auto Generate Allowance And Deduction from excel sheet</h6>
            </div>
          </div>
          {/* <div className="page-btn">
            
          </div> */}
          &nbsp;
          <div className="page-btn">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="add-new-tooltip">Back</Tooltip>}
            >
              <Link
                to={`${all_routes.emp_allowance_deduction}`}
                className="btn btn-secondary"
              >
                <BackIcon className="me-2" />
                Back
              </Link>
            </OverlayTrigger>
          </div>
        </div>
        <FormProvider {...methods}>
          <div className="card border-0">
            <div className="card-body">
              <div className="form-row row">
                <div className="row">
                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <div className="form-group">
                      <label className="form-label">Date</label>

                      <DatePicker
                        picker="month"
                        className="form-control"
                        style={{ display: "flex" }}
                        onChange={(date) => {
                          updateQueryParams(
                            "fromDate",
                            format(startOfMonth(date), "yyyy-MM-dd")
                          );
                          updateQueryParams(
                            "toDate",
                            format(endOfMonth(date), "yyyy-MM-dd")
                          );
                        }}
                        defaultValue={
                          queryParams?.fromDate
                            ? dayjs(queryParams.fromDate)
                            : dayjs()
                        }
                        allowClear={false}
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <div className="form-group">
                      <label className="form-label">Salary Type</label>

                      <Select
                        // className="form-control"
                        style={{ display: "flex" }}
                        onSelect={(selectedOption) => {
                          updateQueryParams("salaryType", selectedOption);
                        }}
                        onClear={() => updateQueryParams("salaryType", "")}
                        options={[
                          { label: "All", value: "" },
                          { label: "Monthly", value: "Monthly" },
                          { label: "Hourly", value: "Hourly" },
                        ]}
                        allowClear={false}
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12">
                    <div className="input-blocks">
                      <label className="form-label">&nbsp;</label>

                      <Link
                        className="btn btn-primary"
                        onClick={generateData}
                        type="button"
                      >
                        <i data-feather="search" className="feather-search" />
                        &nbsp;
                        {isLoading ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Loading...
                          </>
                        ) : (
                          <>Generate Data</>
                        )}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row form-row">
                  <div className="col-lg-12">
                    <div className="d-flex justify-content-end mb-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="add-new-tooltip">Add New</Tooltip>
                        }
                      >
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={addNewRowHandler}
                        >
                          Add New
                        </button>
                      </OverlayTrigger>
                      <button
                        className="btn btn-added ms-2"
                        type="submit"
                        disabled={
                          fields.length === 0 || isSubmitting || isPending
                        }
                      >
                        &nbsp;
                        {isLoading || isPending || isSubmitting ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Loading...
                          </>
                        ) : (
                          <>
                            
                            <Save className="me-2" />
                            Save
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="table-responsive">
                      <Table
                        columns={columns}
                        dataSource={fields}
                        pagination={false}
                        rowKey={(record) => record.rowId}
                      />
                    </div>
                    {errors.allowanceDeductionData && (
                      <div className="text-danger mt-2">
                        {errors.allowanceDeductionData.message}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export default GenerateAllowanceAndDeduction;
